import { combineReducers, createStore, applyMiddleware, compose } from 'redux';
import thunk from 'redux-thunk';
import { Block, Blocks } from '@chameleoncreator/creator-app';
import { watch, getState, getLocalState } from './localStore';
import { createModule } from 'factories/module';
import { flatten } from 'factories/utils';
import { getTopicGroup, findBlocksIn } from 'selectors/block';
import { getInteractionsFromTopicAndState } from 'selectors/block';
import * as Sentry from '@sentry/browser';
import ReactGA from 'react-ga';
import { Mirror } from './mirror';

import mockBlocks from 'mockBlocks';
import moduleBlocks from 'moduleBlocks';

export enum TrackerState {
  DIRTY,
  CLEAN,
  DELETED,
}

export interface Tracker {
  [key: string]: TrackerState;
}

interface Timing {
  firstLoad: number;
  complete: number;
  buttonClick: { [key: string]: number };
}

export interface Report {
  key?: number;
  id: string;
  question?: string | undefined;
  reportable: 'multichoice' | 'sortation';
  type: string;
  response: string;
  result: 0 | 1;
}

export interface NextState {
  customFonts: any[];
  focusedNodeId: string | null;
  lastFocusedNodeId: string | null;
  currentInteractionBlockId: string | null;
  interactionsSeen: string[];
  interactionsLocked: string[];
  interactionsOpen: string[];

  currentInteraction: string;
  isScrolling: boolean;

  currentTopic: string;
  rootBlockId: string;
  projectId: string;
  blocks: { [key: string]: Block };
  hasError: boolean;
  error: any;
  tracker: Tracker;
  apiError: { count: number; err: any };
  mirrorActive: boolean;
  scormSettings: {
    setCompleteWhenAllPagesViewed: boolean;
    setCompleteFromScore: boolean;
    setCompleteOnExit: boolean;
    passMark: number;
  };
  userBehaviour: {
    score: { [key: string]: 0 | 1 };
    interactionTiming: { [key: string]: Timing };
    scormResponses: {
      [key: string]: Report;
    };
  };
  theme: {
    userPalette: any[];
    fontFamily: string;
    fontFamilies: any[];
    recoloredChameleonImages: any;
    //colors
    backgroundColor: string;
    headerBackgroundColor: string;
    headerTextColor: string;
    textColor: string;
    buttonTextColor: string;
    // buttonBorderColor: 'transparent',
    buttonBackgroundColor: string;
    //design
    buttonShape: string;
    buttonShadow: boolean;
    buttonOutline: boolean;
    buttonBorderRadius: number;
    progressBar: boolean;
    topicMenu: boolean;
    highContrast: boolean;
  };
}

const initialState = {
  customFonts: [],
  focusedNodeId: undefined,
  lastFocusedNodeId: undefined,
  currentInteractionBlockId: undefined,

  currentInteraction: undefined,
  isScrolling: false,

  currentTopic: undefined,
  interactionsSeen: [],
  interactionsLocked: [],
  interactionsOpen: [],
  scormSettings: {
    setCompleteWhenAllPagesViewed: true,
    setCompleteFromScore: false,
    setCompleteOnExit: false,
    passMark: 80,
  },
  theme: {
    userPalette: [],
    fontFamily: '',
    fontFamilies: [],
    recoloredChameleonImages: {},
    //colors
    backgroundColor: '#fff',
    headerBackgroundColor: '#fff',
    headerTextColor: '#060709',
    textColor: '#060709',
    buttonTextColor: '#fff',
    // buttonBorderColor: 'transparent',
    buttonBackgroundColor: '#060709',
    //design
    buttonShape: 'pill',
    buttonShadow: false,
    buttonOutline: false,
    highContrast: false,
    buttonBorderRadius: 0,
    progressBar: true,
    topicMenu: true,
  },
  rootBlockId: undefined,
  projectId: undefined,
  themeId: undefined,
  blocks: {},
  links: {},
  hasError: false,
  error: undefined,
  apiError: { count: 0, err: undefined },
  tracker: {},
  mirrorActive: false,
  userBehaviour: {
    score: {},
    interactionTiming: {},
    scormResponses: {},
  },
};

const remoteable = (): boolean => {
  const w = window.hasRemote && window.hasRemote();
  const p = parent.hasRemote && parent.hasRemote();
  return w || p;
};

const hasMode = (mode: string): boolean => {
  const w = window.hasMode && window.hasMode(mode);
  const p = parent.hasMode && window.hasMode(mode);
  return w || p;
};

if (
  !remoteable() // ||
  // true
) {
  const mod = createModule('Example');
  const [flatMod, rootBlockId] = flatten(mod);
  const cur = (getTopicGroup(flatMod).children[0] as Block).id;

  initialState.blocks = flatMod;
  initialState.rootBlockId = rootBlockId;
  initialState.currentTopic = cur;
}

type State = NextState;
type Action = { type: string; data: { [key: string]: any } };

const rehydrate = (state: State, { data }: Action): State => {
  return { ...state, ...data };
};

export const rehydrateAction = data => ({
  type: 'REHYDRATE',
  data: data,
});

const focusBlock = (state: State, { data }: Action): State => {
  const { focusedNodeId, lastFocusedNodeId, currentInteractionBlockId } = state;

  const type = data && (state.blocks as any)[data.id].type;
  let updatedCurrentInteractionBlockId =
    type && type === 'Interaction'
      ? data.id
      : !data || data.id === null
      ? null
      : currentInteractionBlockId;

  return {
    ...state,
    lastFocusedNodeId: lastFocusedNodeId === '' ? data.id : focusedNodeId,
    focusedNodeId: data ? data.id : null,
    currentInteractionBlockId: updatedCurrentInteractionBlockId,
  };
};

export const focusBlockAction = (block: Block) => ({
  type: 'FOCUS_BLOCK',
  data: block,
});

export const setFontsAction = (fonts: any[]) => ({
  type: 'SET_FONTS',
  data: fonts,
});

export const setFonts = (state: State = initialState, { data }: Action) => {
  return {
    ...state,
    customFonts: data as any,
  };
};

const setBlock = (state: State = initialState, { data }: Action): State => {
  const nextState = {
    ...state,
    blocks: { ...state.blocks },
    tracker: { ...state.tracker },
  };
  nextState.blocks[data.id] = data as Block;
  nextState.tracker[data.id] = TrackerState.DIRTY;
  return nextState;
};

export const setBlockLocalAction = (block: Block) => {
  return {
    type: 'SET_BLOCK',
    data: block,
  };
};

export const setBlockAction = (block: Block): any => {
  return (dispatch, getState) => {
    const s = getState();
    block.project_id = s.projectId;
    block.__isSaving = true;

    if (window.location.hostname === 'v2.chameleoncreator.com')
      ReactGA.event({
        category: 'Editor',
        action: 'SetBlock',
        label: `${s.projectId}`,
        nonInteraction: true,
      });

    dispatch(setBlockLocalAction(block));

    if (remoteable()) {
      Mirror(dispatch, getState);
    }
  };
};

export const setErrorAction = err => {
  return {
    type: 'SET_ERROR',
    data: err,
  };
};

export const setError = (state: State = initialState, { data }: Action) => {
  return {
    ...state,
    hasError: true,
    error: data,
  };
};

const setBlocks = (state: State = initialState, { data }: Action): State => {
  const blocks = data as Blocks;
  const nextState = {
    ...state,
    blocks: { ...state.blocks },
    tracker: { ...state.tracker },
  };

  Object.keys(blocks).forEach(id => {
    const block = blocks[id];
    nextState.blocks[block.id] = block;
    nextState.tracker[block.id] = TrackerState.DIRTY;
  });

  return nextState;
};

export const setBlocksLocalAction = (blocks: Blocks) => {
  return {
    type: 'SET_BLOCKS',
    data: blocks,
  };
};

export const setBlocksAction = (blocks: Blocks): any => {
  return async (dispatch, getState) => {
    const s = getState();

    const bs = Object.keys(blocks).map(key => {
      blocks[key].__isSaving = true;
      blocks[key].project_id = s.projectId;
      return blocks[key];
    });

    const localbs = bs.reduce((acc, b) => {
      acc[b.id] = b;
      return acc;
    }, {});

    dispatch(setBlocksLocalAction(localbs));

    if (remoteable()) {
      Mirror(dispatch, getState);
    }
  };
};

const removeBlockLocal = (
  state: State = initialState,
  { data }: Action
): State => {
  const nextState = {
    ...state,
    blocks: { ...state.blocks },
  };

  delete nextState.blocks[data.id];

  return nextState;
};

const removeBlock = (state: State = initialState, { data }: Action): State => {
  const nextState = {
    ...state,
    blocks: { ...state.blocks },
    tracker: { ...state.tracker },
  };

  nextState.tracker[data.id] = TrackerState.DELETED;

  const all = findBlocksIn(nextState.blocks, data as Block, () => true);

  all.forEach(b => {
    nextState.tracker[b.id] = TrackerState.DELETED;
  });

  return nextState;
};

export const removeBlockAction = (block: Block): any => {
  return async (dispatch, getState) => {
    const u = { type: 'REMOVE_BLOCK', data: block };
    dispatch(u);

    if (remoteable()) {
      Mirror(dispatch, getState);
    }
  };
};

const setCurrentInteraction = (
  state: State = initialState,
  { data }: Action
): State => {
  return {
    ...state,
    ...(data.updateFocusedNodeId && {
      focusedNodeId: data.id,
    }),
    currentInteraction: data.id,
  };
};

const setIsScrolling = (
  state: State = initialState,
  { data }: Action
): State => {
  return {
    ...state,
    isScrolling: data.isScrolling,
  };
};

const setActiveTopic = (
  state: State = initialState,
  { data }: Action
): State => {
  return {
    ...state,
    currentTopic: data.id,
  };
};

export const setLinksAction = (links: any) => {
  return {
    type: 'SET_LINKS',
    data: links,
  };
};

const setLinks = (state: State = initialState, { data }: Action) => {
  return {
    ...state,
    links: data,
  };
};

export const setCurrentInteractionAction = (
  id: string,
  updateFocusedNodeId
) => {
  return {
    type: 'SET_CURRENT_INTERACTION',
    data: { id: id, updateFocusedNodeId },
  };
};

export const setIsScrollingAction = (isScrolling: boolean) => {
  return {
    type: 'SET_IS_SCROLLING',
    data: { isScrolling },
  };
};

export const setActiveTopicAction = (id: string) => {
  return {
    type: 'SET_ACTIVE_TOPIC',
    data: { id: id },
  };
};

export const rehydrateFromLMSAction = data => {
  // console.log('rehydrateFromLMSAction :', {
  //   data,
  // });

  return {
    type: 'REHYDRATE_FROM_LMS',
    data,
  };
};

const rehydrateFromLMS = (
  state: State = initialState,
  { data }: Action
): State => {
  const { interactionsSeen } = data as any;

  console.log('rehydratedFromLMS :', {
    data,
    // interactionsSeen,
  });

  return {
    ...state,
    ...interactionsSeen,
  } as any;
};

const updateInteractionsSeen = (
  state: State = initialState,
  { data }: Action
): State => {
  const { interactionsSeen = [] } = state as any;

  // console.log({ interactionsSeen });

  if (Array.isArray(data)) {
    return {
      ...state,
      interactionsSeen: [
        ...Array.from(new Set([...interactionsSeen, ...data])),
      ],
    } as any;
  }

  if (interactionsSeen.includes(data.id)) {
    return state;
  } else {
    return {
      ...state,
      interactionsSeen: [...interactionsSeen, data.id],
    } as any;
  }
};

export const updateInteractionsOpenAction = (id: string) => {
  // console.log('updateInteractionsOpenAction :', id);

  return {
    type: 'UPDATE_INTERACTIONS_OPEN',
    data: id,
  };
};

const updateInteractionsOpen = (
  state: State = initialState,
  { data }: Action
): State => {
  const { blocks, currentTopic, interactionsOpen } = state as any;

  // interactions in currentTopic
  // get the next interaction.id based on ( current ) data.id
  // add it to the list. . .

  // const block = getBlock(
  //   blocks,
  //   (Object.values(blocks) as any).find(($: any) => $.id === currentTopic)
  // );
  const interactions = getInteractionsFromTopicAndState(
    state as any,
    (Object.values(blocks) as any).find(($: any) => $.id === currentTopic)
  );

  const nextInteraction =
    interactions[interactions.findIndex((i: any) => i.id === data) + 1];

  if (!nextInteraction) return state;

  const nextInteractionId = nextInteraction && nextInteraction.id;

  if (interactionsOpen.includes(nextInteractionId)) {
    return state;
  } else {
    return {
      ...state,
      interactionsOpen: [...interactionsOpen, nextInteractionId],
    } as any;
  }
};

export const updateUserPaletteAction = (data): any => {
  return (dispatch, getState) => {
    const n = {
      type: 'UPDATE_USER_PALETTE',
      data,
    };

    dispatch(n);

    if (remoteable()) {
      const { theme, themeId } = getState();
      const nextTheme = {
        ...theme,
        userPalette: data,
      };

      window.client.themes
        .set(`/api/v2/themes/${themeId}`, nextTheme)
        .catch(err => {
          Sentry.withScope(scope => {
            scope.setExtra('message', err.err);
            Sentry.captureException(err);
          });
          dispatch(setErrorAction(err));
        });
    }
  };
};

export const updateRecoloredChameleonImagesAction = (data): any => {
  return (dispatch, getState) => {
    const n = {
      type: 'UPDATE_RECOLORED_CHAMELEON_IMAGES',
      data,
    };

    dispatch(n);

    if (remoteable()) {
      const { theme, themeId } = getState();
      const nextTheme = {
        ...theme,
        recoloredChameleonImages: data,
      };

      window.client.themes
        .set(`/api/v2/themes/${themeId}`, nextTheme)
        .catch(err => {
          Sentry.withScope(scope => {
            scope.setExtra('message', err.err);
            Sentry.captureException(err);
          });
          dispatch(setErrorAction(err));
        });
    }
  };
};

const updateRecoloredChameleonImages = (
  state: State = initialState,
  { data }: Action
): State => {
  const nextState = {
    ...state,
    theme: {
      ...(state as any).theme,
      recoloredChameleonImages: {
        ...(state as any).theme.recoloredChameleonImages,
        ...data,
      },
    },
  };

  return nextState;
};

export const setThemeAction = theme => {
  return {
    type: 'SET_THEME',
    data: theme,
  };
};

const setTheme = (state: State = initialState, { data }: Action): State => {
  const nextState = {
    ...state,
    themeId: data.id,
    theme: { ...(state as any).theme, ...data.data },
  };

  return nextState;
};

export const setThemeIdAction = id => {
  return {
    type: 'SET_THEME_ID',
    data: id,
  };
};

const setThemeId = (state: State = initialState, { data }: Action): State => {
  const nextState = {
    ...state,
    themeId: data,
  };

  return nextState;
};

const updateUserPalette = (
  state: State = initialState,
  { data }: Action
): State => {
  const nextState = {
    ...state,
    theme: {
      ...(state as any).theme,
      userPalette: data,
    },
  };

  return nextState;
};

export const updateCourseThemeAction = data => {
  return (dispatch, getState) => {
    const next = {
      type: 'UPDATE_COURSE_THEME',
      data,
    };
    dispatch(next);

    if (remoteable()) {
      const { theme, themeId } = getState();
      const nextTheme = {
        ...theme,
        ...data,
      };

      window.client.themes
        .set(`/api/v2/themes/${themeId}`, nextTheme)
        .catch(err => {
          Sentry.withScope(scope => {
            scope.setExtra('message', err.err);
            Sentry.captureException(err);
          });
          dispatch(setErrorAction(err));
        });
    }
  };
};

//

export const updateScormSettingsAction = scormSettings => {
  return {
    type: 'UPDATE_SCORM_SETTINGS',
    data: scormSettings,
  };
};

const updateScormSettings = (
  state: State = initialState,
  { data }: Action
): State => {
  const nextState = {
    ...state,
    scormSettings: {
      ...(state as any).scormSettings,
      ...data,
    },
  };

  return nextState;
};

//

const updateCourseTheme = (
  state: State = initialState,
  { data }: Action
): State => {
  const nextState = {
    ...state,
    theme: {
      ...(state as any).theme,
      ...data,
    },
  };

  return nextState;
};

const setRootBlock = (state: State = initialState, { data }: Action): State => {
  const nextState = {
    ...state,
    rootBlockId: data as any,
  };
  return nextState;
};

export const setRootBlockAction = (id: string) => {
  return {
    type: 'SET_ROOT_BLOCK',
    data: id,
  };
};

const setProjectId = (state: State = initialState, { data }: Action): State => {
  const nextState = {
    ...state,
    projectId: data as any,
  };

  return nextState;
};

export const setProjectIdAction = (id: string) => {
  return {
    type: 'SET_PROJECT_ID',
    data: id,
  };
};

export const setAPIErrorAction = err => {
  return {
    type: 'SET_API_ERROR',
    data: err,
  };
};

const setAPIError = (state: State = initialState, { data }: Action): State => {
  const nextState = {
    ...state,
    apiError: {
      count: state.apiError.count + 1,
      err: data,
    },
  };

  if (nextState.apiError.count >= 3) {
    nextState.hasError = true;
    nextState.error = data;
  }

  return nextState;
};

export const setTrackerAction = tracker => {
  return {
    type: 'SET_TRACKER',
    data: tracker,
  };
};

const setTracker = (state: State = initialState, { data }: Action): State => {
  const nextState = {
    ...state,
    tracker: data,
  };

  return nextState;
};

const mirrorActive = (state: State = initialState, { data }: Action): State => {
  return {
    ...state,
    mirrorActive: data as any,
  };
};

export const loadSuspendDataAction = suspendData => {
  return {
    type: 'LOAD_SUSPEND_DATA',
    data: suspendData,
  };
};

const loadSuspendData = (
  state: State = initialState,
  { data }: Action
): State => {
  return {
    ...state,
    ...{
      interactionsSeen: data.interactionsSeen,
      interactionsLocked: data.interactionsLocked,
      userBehaviour: {
        ...state.userBehaviour,
        ...{
          score: data.score,
          interactionsReporting: data.interactionsReporting,
        },
      },
    },
  };
};

export const updateScoreAction = (id: string, score: 0 | 1) => {
  return {
    type: 'UPDATE_SCORE',
    data: { id, score },
  };
};

const updateScore = (state: State = initialState, { data }: Action) => {
  return {
    ...state,
    userBehaviour: {
      ...state.userBehaviour,
      score: {
        ...state.userBehaviour.score,
        [data.id]: data.score,
      },
    },
  };
};

export const addScormResponseAction = (scormAction: Report) => {
  return {
    type: 'ADD_SCORM_RESPONSE',
    data: scormAction,
  };
};

const addScormResponse = (state: State = initialState, { data }: Action) => {
  return {
    ...state,
    userBehaviour: {
      ...state.userBehaviour,
      scormResponses: {
        ...state.userBehaviour.scormResponses,
        [data.id]: data,
      },
    },
  };
};

const actionHandlers = {
  REHYDRATE: rehydrate,
  FOCUS_BLOCK: focusBlock,
  SET_BLOCK: setBlock,
  SET_BLOCKS: setBlocks,
  REMOVE_BLOCK: removeBlock,
  REMOVE_BLOCK_LOCAL: removeBlockLocal,
  SET_ACTIVE_TOPIC: setActiveTopic,
  SET_CURRENT_INTERACTION: setCurrentInteraction,
  SET_IS_SCROLLING: setIsScrolling,
  UPDATE_INTERACTIONS_SEEN: updateInteractionsSeen,
  UPDATE_INTERACTIONS_OPEN: updateInteractionsOpen,
  SET_ROOT_BLOCK: setRootBlock,
  SET_PROJECT_ID: setProjectId,
  UPDATE_SCORM_SETTINGS: updateScormSettings,
  UPDATE_USER_PALETTE: updateUserPalette,
  UPDATE_COURSE_THEME: updateCourseTheme,
  SET_THEME: setTheme,
  SET_THEME_ID: setThemeId,
  REHYDRATE_FROM_LMS: rehydrateFromLMS,
  SET_LINKS: setLinks,
  SET_ERROR: setError,
  SET_API_ERROR: setAPIError,
  SET_TRACKER: setTracker,
  MIRROR_ACTIVE: mirrorActive,
  SET_FONTS: setFonts,
  UPDATE_RECOLORED_CHAMELEON_IMAGES: updateRecoloredChameleonImages,
  LOAD_SUSPEND_DATA: loadSuspendData,
  UPDATE_SCORE: updateScore,
  ADD_SCORM_RESPONSE: addScormResponse,
};

// translate actions into handlers (standard redux reducer)
export const reducer = (state: State = initialState, action: Action): State => {
  const handler = actionHandlers[action.type];
  return handler ? handler(state, action) : { ...state };
};

// create the store from the reducer above
// const store = createStore(reducer, applyMiddleware(thunk));

const composeEnhancers =
  window['__REDUX_DEVTOOLS_EXTENSION_COMPOSE__'] || compose;
const store = createStore(reducer, composeEnhancers(applyMiddleware(thunk)));

(() => {
  // mockBlocks moduleBlocks
  // return store.dispatch(rehydrateAction(mockBlocks as any));

  if (!remoteable() && hasMode('PREVIEW')) {
    const savedStore = getState();
    // if we have saved state, lets load it in.
    if (savedStore && savedStore !== undefined) {
      store.dispatch(rehydrateAction(savedStore));
    }
  }

  if (!remoteable() && hasMode('DEVELOPMENT')) {
    // rehydrate
    const savedStore = getLocalState();

    // if we have saved state, lets load it in.
    if (savedStore && savedStore !== undefined) {
      store.dispatch(rehydrateAction(savedStore));
    }

    //Save changes
    watch(store as any);
  }
})();

export default store;
